import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaTimes, FaShoppingBag } from 'react-icons/fa';
import { useWishlist } from '../context/WishlistContext';
import displayINRCurrency from '../helpers/displayCurrency';

const WishlistReminderPopup = ({ onClose }) => {
  const navigate = useNavigate();
  const { wishlistItems } = useWishlist();
  const [showItems, setShowItems] = useState([]);

  useEffect(() => {
    // Show up to 3 items from wishlist
    setShowItems(wishlistItems.slice(0, 3));
  }, [wishlistItems]);

  if (wishlistItems.length === 0) return null;

  const handleViewWishlist = () => {
    navigate('/wishlist');
    onClose();
  };

  const handleProductClick = (item) => {
    const productUrl = item.variantPublicId 
      ? `/product/${item.publicId}-${item.variantPublicId}`
      : `/product/${item.publicId}`;
    navigate(productUrl);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all animate-scaleIn">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-pink-500 via-red-500 to-pink-600 rounded-t-2xl p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <FaHeart className="text-white text-xl animate-pulse" />
            </div>
            <div>
              <h3 className="text-white text-xl font-bold">Don't Forget!</h3>
              <p className="text-white text-opacity-90 text-sm">
                You have {wishlistItems.length} item{wishlistItems.length > 1 ? 's' : ''} in your wishlist
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-700 mb-4 text-sm">
            Complete your purchase before these items go out of stock!
          </p>

          {/* Wishlist Items Preview */}
          <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
            {showItems.map((item, index) => (
              <div
                key={`${item.productId}-${item.variantId}-${index}`}
                onClick={() => handleProductClick(item)}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors border border-gray-200"
              >
                <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  <img
                    src={item.images?.[0] || '/placeholder-product.png'}
                    alt={item.name || item.label}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder-product.png';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
                    {item.name || item.label}
                  </h4>
                  <p className="text-orange-600 font-bold text-sm mt-1">
                    {displayINRCurrency(item.sellingPrice)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleViewWishlist}
              className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-lg"
            >
              <FaHeart className="w-4 h-4" />
              View Wishlist
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors duration-200"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WishlistReminderPopup;

